import { useRef } from "react";

// Horizontal swipe for photo galleries on touch screens. The arrows stay in
// place for mouse users — this is additive.
//
// Usage:
//   const swipe = useSwipe({ onLeft: next, onRight: prev });
//   <div {...swipe}> ... </div>
//
// A gesture only counts as a swipe when it is clearly horizontal, so vertical
// page scrolling through a gallery is never hijacked.
const MIN_DISTANCE = 45; // px of horizontal travel before it counts
const MAX_OFF_AXIS = 1.0; // |dx| must exceed |dy| by this factor

export default function useSwipe({ onLeft, onRight, minDistance = MIN_DISTANCE } = {}) {
  const start = useRef(null);

  const onTouchStart = (e) => {
    const t = e.touches && e.touches[0];
    if (!t) return;
    start.current = { x: t.clientX, y: t.clientY };
  };

  const onTouchEnd = (e) => {
    const s = start.current;
    start.current = null;
    if (!s) return;
    const t = e.changedTouches && e.changedTouches[0];
    if (!t) return;
    const dx = t.clientX - s.x;
    const dy = t.clientY - s.y;
    if (Math.abs(dx) < minDistance) return;
    if (Math.abs(dx) <= Math.abs(dy) * MAX_OFF_AXIS) return; // vertical scroll
    if (dx < 0) onLeft?.();
    else onRight?.();
  };

  // Cancel a tracked gesture if the browser takes over (e.g. it becomes a scroll).
  const onTouchCancel = () => {
    start.current = null;
  };

  return { onTouchStart, onTouchEnd, onTouchCancel };
}
